import { useLayoutEffect, useRef, type ReactNode } from 'react'
import { useInBar } from '@/lib/inBar'
import { cn } from '@/lib/utils'

export type SwipeTab<K extends string> = { key: K; label: ReactNode; name?: string }

// The tabs over a Modern page (Photos' All / Albums / People, Moments' All / Then / Now): plain words with a line
// under the one you're on, which slides to the tab you pick. More than fit scroll sideways.
export function SlidingTabs<K extends string>({
  tabs,
  active,
  onChoose,
  label = 'Show',
}: {
  tabs: SwipeTab<K>[]
  active: K
  onChoose: (key: K) => void
  label?: string
}) {
  const inBar = useInBar()
  const row = useRef<HTMLDivElement>(null)
  const pill = useRef<HTMLSpanElement>(null)
  const placed = useRef(false)
  useLayoutEffect(() => {
    const button = row.current?.querySelector<HTMLElement>(`[data-tab="${active}"]`)
    const el = pill.current
    if (!button || !el) return
    if (!placed.current) el.style.transition = 'none'
    el.style.width = `${button.offsetWidth}px`
    el.style.transform = `translateX(${button.offsetLeft}px)`
    // When the tabs run wider than the screen, keep the one you're on in sight.
    const box = row.current!
    if (button.offsetLeft < box.scrollLeft) box.scrollTo({ left: button.offsetLeft - 16, behavior: 'smooth' })
    else if (button.offsetLeft + button.offsetWidth > box.scrollLeft + box.clientWidth)
      box.scrollTo({ left: button.offsetLeft + button.offsetWidth - box.clientWidth + 16, behavior: 'smooth' })
    if (!placed.current) {
      void el.offsetWidth
      el.style.transition = ''
      placed.current = true
    }
  }, [active, tabs])
  return (
    // The group around the row is what the phone's top bar draws as a pill once the page runs under it (modern.css),
    // so its rounded ends are never cut off by the row's sideways scroll.
    <div className="mp-tabgroup flex min-w-0">
      <div
        ref={row}
        className={cn(
          'mp-chips flex min-w-0 [scrollbar-width:none] overflow-x-auto [&::-webkit-scrollbar]:hidden',
          !inBar && 'max-sm:pl-5',
        )}
        role="group"
        aria-label={label}
      >
        <span ref={pill} className="mp-chip-ind" aria-hidden />
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            data-tab={t.key}
            aria-pressed={t.key === active}
            aria-label={t.name}
            className="mp-chip"
            onClick={() => onChoose(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// The far end of the tab row (Filter, Select). In the phone's top bar, the bar keeps the edge, so it needs no room of
// its own.
export function TabsEnd({ children }: { children: ReactNode }) {
  const inBar = useInBar()
  return <div className={cn('ml-auto flex shrink-0 items-center gap-2', !inBar && 'max-sm:pr-5')}>{children}</div>
}
